// Behaviour shared by both media players: the favourite toggle and the analytics
// payload.
//
// Extracted rather than duplicated because the AI-overlay player reimplements its
// own transport and would otherwise repeat this, then drift on the first copy or
// payload change. Deliberately NOT a component: the two players lay their controls
// out differently (the standard one renders its bar through vidstack), and freezing
// the appearance is exactly what should not be shared.
//
// It also fixes an inconsistency that already reached production data: both players
// emit `Dashcam Video Played` / `Dashcam Video Watched Fully`, but the AI-overlay
// one used to send only `{ origin, player }` while the standard one sent the media
// context. Same event name, two shapes, one dirty funnel. Now the payload comes
// from here and `player` is just a property.
import { Analytics } from '@cobliteam/analytics';
import type { MediaEvent, MediaView } from '@cobliteam/common-types';
import { useUpdateFavoriteMedia } from '@cobliteam/hooks/src/useCamera/useCameraMedia';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import { useCallback } from 'react';
import { MediaTableType } from '../../utils/mediaUtils';

export interface MediaActions {
  isFavorite?: boolean;
  toggleFavorite?: () => void;
  // Tracks with the media context every media event should carry. Extra props win
  // over the defaults, so a caller can add `player` or `component`.
  trackEvent: (eventName: string, properties?: Record<string, unknown>) => void;
}

export function useMediaActions(mediaView?: MediaView, origin?: string): MediaActions {
  const { t } = useTranslation('media');
  const isEvent = mediaView?.mediaInfo.mediaType === 'EVENT_VIDEO';
  const { mutateAsync: toggleFavoriteMutation } = useUpdateFavoriteMedia(isEvent ? MediaTableType.EVENT_MEDIA : MediaTableType.FOOTAGE_MEDIA);

  const isFavorite = mediaView?.mediaInfo.isFavorite;

  const trackEvent = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      const { mediaType, mediaDetails, isFavorite: favorite, severity, id } = mediaView?.mediaInfo ?? {};
      const eventType = isEvent ? (mediaDetails as MediaEvent | undefined)?.eventTrigger : mediaType;

      Analytics.track(eventName, {
        mediaId: id,
        eventType,
        eventSeverity: severity,
        isFavorite: favorite,
        origin,
        ...properties,
      });
    },
    [isEvent, mediaView, origin]
  );

  const toggleFavorite = useCallback(async () => {
    if (!mediaView) return;

    trackEvent('Favorite Media Button Clicked', { newFavoriteStatus: !isFavorite });

    try {
      await toggleFavoriteMutation({ updateUserFavoriteMedia: { mediaId: mediaView.mediaInfo.id, isFavorite: !isFavorite } });
      Alert.success(isFavorite ? t('player.video_disliked') : t('player.video_liked'));
    } catch {
      Alert.error(isFavorite ? t('player.error_disliking_video') : t('player.error_liking_video'));
    }
  }, [isFavorite, mediaView, t, toggleFavoriteMutation, trackEvent]);

  return {
    isFavorite,
    // No MediaView → nothing to favourite, and the caller hides the control.
    toggleFavorite: mediaView ? toggleFavorite : undefined,
    trackEvent,
  };
}

export default useMediaActions;
